import React, { forwardRef, useState, useEffect } from "react";
import {
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Spin,
  Tooltip,
  Space,
} from "antd";
import Settings from "./components/Settings";
import HouseVisit from "../houseVisit/index";
import ProffesionalEmployee from "../../components/tables/ProffesionalEmployee";
import Date from "./tableItems/Date";
import { useIntl } from "react-intl";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { loadPage, reset, setPage } from "./houseVisitsSlice";
import {
  PlusOutlined,
  SettingFilled,
  FormOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  PrinterOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import Surface from "../../components/layouts/Surface";
import EmptyState from "../../components/templates/EmptyState";
import { ReactComponent as HomevisitImg } from "../../assets/no-home-visits.svg";
import { FormattedMessage } from "react-intl";
import { deleteRecord, loadRecord } from "../houseVisit/houseVisitSlice";
import { getHouseVisit, loadImage } from "../../components/util/helpers";
import jsPDF from "jspdf";
import { useUserBranches } from "../../hooks/accessControl";

var unflatten = require("flat").unflatten;

const { Text } = Typography;
var flatten = require("flat");

const Visits = forwardRef((props, ref) => {
  const { nonActive } = props;
  const dispatch = useDispatch();
  let location = useLocation();
  const userBranches = useUserBranches();
  const [modal, contextHolder] = Modal.useModal();
  const activePatient = useSelector(
    (state) => state.patientDetails.activePatient
  );
  const organization = useSelector((state) => state.organization.details);
  const intl = useIntl();
  const [flattenList, setFlattenList] = useState([]);
  const [mode, setMode] = useState(null);
  const [formType, setFormType] = useState(null);
  const [visible, setVisible] = useState(false);
  const { page: houseVisitsPage } = useSelector((state) => state.houseVisits);
  const { items } = houseVisitsPage;
  const activeHouseVisit = useSelector(
    (state) => state.houseVisit.activeHouseVisit
  );
  const loading = useSelector(
    (state) => state.pendingActions["houseVisits/loadPage"]
  );

  useEffect(() => {
    if (activePatient.id) {
      dispatch(loadPage());
    }
    return function cleanup() {
      dispatch(reset());
    };
  }, [dispatch, activePatient.id]);

  useEffect(() => {
    dispatch(loadPage());
  }, [dispatch, houseVisitsPage.filter]);

  useEffect(() => {
    let flatList = [];
    items.forEach((item) => {
      const flattenItem = flatten(item, { safe: true });
      flatList.push(flattenItem);
    });
    setFlattenList(flatList);
  }, [items]);

  const onShowFormClick = (record) => {
    const isAdmin = location.pathname.includes("admin");
    const params = {
      patientId: record["patient.id"],
      branchId: record["patient.branch"],
      admin: isAdmin,
      houseVisitsId: record.id,
    };
    dispatch(loadRecord(params));

    setVisible(true);
    setMode("edit");
    setFormType("houseVisit");
  };

  const onPrintClick = async (record) => {
    const avatar_url = organization.avatar_url;
    const logo = await loadImage(avatar_url);
    let diaryPdf = new jsPDF({ orientation: "p" });
    const diaryRecord = unflatten(record, { safe: true });
    let approvalImg = null;
    let verifyImg = null;
    if (diaryRecord.approval_signature) {
      approvalImg = await loadImage(diaryRecord.approval_signature.url);
    }
    if (diaryRecord.verification_signature) {
      verifyImg = await loadImage(diaryRecord.verification_signature.url);
    }
    const pdf = await getHouseVisit(
      diaryPdf,
      intl,
      { ...diaryRecord, employee: diaryRecord.planned_employee },
      userBranches,
      organization,
      logo,
      approvalImg,
      verifyImg
    );
    return window.open(pdf.output("bloburl"), "_blank");
  };

  const onAddClick = () => {
    setVisible(true);
    setMode("new");
    setFormType("houseVisit");
  };

  const onSettingsClick = () => {
    setVisible(true);
    setFormType("settings");
  };

  const hide = () => {
    setVisible(false);
    setFormType(null);
  };

  const onDeleteHouseVisit = (record) => {
    const title = intl.formatMessage({
      id: "app.house_visits.table.deleteHouseVisitTitle",
    });
    const content = intl.formatMessage({
      id: "app.house_visits.table.deleteHouseVisitContent",
    });
    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk() {
        return onConfirm(record);
      },
      onCancel() {},
    });
  };
  const onConfirm = async (record) => {
    const params = {
      branchId: activePatient.branch,
      houseVisitId: record.id,
      patientId: activePatient.id,
    };
    const action = await dispatch(deleteRecord(params));
    if (!action.error) {
      dispatch(loadPage());
    }
  };

  const columns = [
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.employee",
      }),
      dataIndex: "planned_employee",
      width: "200px",
      fixed: "left",
      render(text, record) {
        return (
          <ProffesionalEmployee
            record={unflatten(record, { safe: true })["planned_employee"]}
            withAvatar={true}
            long={true}
          />
        );
      },
      sorter: (a, b) =>
        a["planned_employee.surename"].localeCompare(
          b["planned_employee.surename"]
        ),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.plannedDate",
      }),
      dataIndex: "date",
      width: "170px",
      render(text, record) {
        return <Date text={text} format={"L"} />;
      },
      sorter: (a, b) => a["date"].localeCompare(b["date"]),
      sortDirections: ["descend", "ascend"],
    },

    {
      title: intl.formatMessage({
        id: "app.house_visits.table.dayInWeek",
      }),
      dataIndex: "date",
      width: "100px",
      render(text, record) {
        return <Text>{text && moment.weekdays(moment(text).weekday())}</Text>;
      },
    },

    {
      title: intl.formatMessage({
        id: "app.house_visits.table.startHour",
      }),
      dataIndex: "date",
      width: "120px",
      render(text, record) {
        return <Date text={text} format={"HH:mm"} />;
      },
    },

    {
      title: intl.formatMessage({
        id: "app.house_visits.table.endHour",
      }),
      dataIndex: "date",
      width: "120px",
      render(text, record) {
        const end = moment(text).add(record["duration_minutes"], "minutes");
        return <Date text={text && end} format={"HH:mm"} />;
      },
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.care_giver_was_presented",
      }),
      dataIndex: "care_giver_was_presented",
      width: "80px",
      render(text, record) {
        return (
          <Text>
            {text
              ? intl.formatMessage({
                  id: "app.forms.yes",
                })
              : intl.formatMessage({
                  id: "app.forms.no",
                })}
          </Text>
        );
      },
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.phone_visit",
      }),
      dataIndex: "report.phone_visit",
      width: "80px",
      filters: [
        { text: <FormattedMessage id="app.forms.yes" />, value: true },
        {
          text: <FormattedMessage id="app.forms.no" />,
          value: false,
        },
      ],
      render(text, record) {
        return (
          <Text>
            {text
              ? intl.formatMessage({
                  id: "app.forms.yes",
                })
              : intl.formatMessage({
                  id: "app.forms.no",
                })}
          </Text>
        );
      },
    },

    {
      title: intl.formatMessage({
        id: "app.house_visits.table.visitApproved",
      }),
      dataIndex: "approved",
      width: "90px",
      fixed: "right",
      render(text, record) {
        return text === true ? (
          <Tooltip
            title={
              record["approval_signature.url"] && (
                <FormattedMessage id="app.house_visits.table.signed_digitally" />
              )
            }
          >
            <Tag
              color="success"
              icon={
                record["approval_signature.url"] ? (
                  <SafetyCertificateOutlined />
                ) : null
              }
            >
              {intl.formatMessage({
                id: "app.house_visits.table.approved",
              })}
            </Tag>
          </Tooltip>
        ) : text === false ? (
          <Tag color="error">
            {intl.formatMessage({
              id: "app.house_visits.table.notApproved",
            })}
          </Tag>
        ) : null;
      },
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.verify",
      }),
      dataIndex: "verified",
      width: "90px",
      fixed: "right",
      render(text, record) {
        return record["verified"] ? (
          <Tooltip
            title={
              record["verification_signature.url"] && (
                <FormattedMessage id="app.house_visits.table.signed_digitally" />
              )
            }
          >
            <Tag
              color="processing"
              icon={
                record["verification_signature.url"] ? (
                  <SafetyCertificateOutlined />
                ) : null
              }
            >
              {intl.formatMessage({
                id: "app.house_visits.table.verified",
              })}
            </Tag>
          </Tooltip>
        ) : null;
      },
    },
    {
      title: intl.formatMessage({
        id: "app.house_visits.table.actions",
      }),
      key: "action",
      width: "145px",
      fixed: "right",
      render: (text, record) => (
        <Space size="small">
          <Tooltip
            title={<FormattedMessage id="app.house_visits.table.editTooltip" />}
          >
            <Button
              onClick={() => onShowFormClick(record)}
              type="text"
              icon={<FormOutlined />}
            />
          </Tooltip>
          <Tooltip
            title={
              <FormattedMessage id="app.house_visits.table.printTooltip" />
            }
          >
            <Button
              onClick={() => onPrintClick(record)}
              type="text"
              icon={<PrinterOutlined />}
            />
          </Tooltip>
          {record["approved"] || record["report.filled"] ? null : (
            <Tooltip
              title={
                <FormattedMessage id="app.house_visits.table.deleteTooltip" />
              }
            >
              <Button
                style={{ color: "red" }}
                onClick={() => onDeleteHouseVisit(record)}
                type="text"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const onChange = (pagination, filters, sorter, extra) => {
    let phoneVisitFilter = { freeText: null };
    const key = Object.keys(filters)[0];
    const values = filters[key];
    if (values?.length > 0) {
      phoneVisitFilter["$or"] = values.map((value) => ({ [key]: value }));
    }
    dispatch(setPage({ ...houseVisitsPage, filter: phoneVisitFilter }));
  };

  return (
    <div>
      {contextHolder}
      {items.length < 1 && loading ? (
        <div
          style={{
            height: "448px",
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "center",
          }}
        >
          <Spin size="large" />
        </div>
      ) : items.length < 1 ? (
        <div>
          <div
            style={{
              position: "absolute",
              margin: "10px 30px",
            }}
          >
            <Button
              type="text"
              icon={<SettingFilled />}
              onClick={onSettingsClick}
              disabled={nonActive}
            />
          </div>
          <EmptyState
            icon={<HomevisitImg />}
            onClick={onAddClick}
            text={
              <FormattedMessage id="app.house_visits.addNewVisitDescription" />
            }
            buttonText={<FormattedMessage id="app.house_visits.addNewVisit" />}
          />
        </div>
      ) : (
        <Surface>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "10px",
            }}
          >
            <Button
              type="text"
              icon={<SettingFilled />}
              onClick={onSettingsClick}
              disabled={nonActive}
            />
            <Button
              type="primary"
              onClick={onAddClick}
              size="large"
              disabled={nonActive}
            >
              <PlusOutlined />
              {intl.formatMessage({
                id: "app.house_visits.addNewVisit",
              })}
            </Button>
          </div>
          <Table
            bordered
            pagination={false}
            columns={columns}
            dataSource={flattenList}
            rowKey={(record) => record.id}
            scroll={{ y: window.innerHeight - 446 }}
            loading={loading}
            onChange={onChange}
          />
        </Surface>
      )}
      <Modal
        closable={false}
        maskClosable={false}
        footer={null}
        width={"1000px"}
        height={"90%"}
        style={{ top: 20 }}
        styles={{
          body: {
            padding: 0,
          },
        }}
        title={null}
        open={visible}
        onCancel={hide}
        destroyOnClose={true}
      >
        {formType === "houseVisit" ? (
          <HouseVisit
            hide={hide}
            activeHouseVisit={activeHouseVisit}
            mode={mode}
            loadPage={loadPage}
          />
        ) : (
          <Settings hide={hide} />
        )}
      </Modal>
    </div>
  );
});

export default Visits;
